import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Shield, Trash2, Mail, Lock, Server, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import ExploreMore from "@/components/seo/ExploreMore";


const SUPPORT_EMAIL = "contact@thehalaldividendcalendar.com";
const PAGE_URL = "https://getpiefolio.com/halal-dividend-calendar/privacy";

const HalalDividendCalendarPrivacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy – Halal Dividend Calendar</title>
        <meta
          name="description"
          content="Privacy policy for the Halal Dividend Calendar mobile app: what data we collect, how we use it, how to delete your account, and how to contact us."
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy – Halal Dividend Calendar" />
        <meta property="og:description" content="How Halal Dividend Calendar collects, uses, and protects your data." />
        <meta property="og:url" content={PAGE_URL} />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="PieFolio AI" className="h-8 w-auto" />
              <span className="font-bold text-xl">PieFolio AI</span>
            </Link>
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-extrabold">Halal Dividend Calendar Privacy Policy</h1>
        </div>
        <p className="text-muted-foreground mb-10">
          This Privacy Policy applies to the <strong>Halal Dividend Calendar</strong> mobile app (the "App"), operated by
          PieFolio AI. Last updated: August 2026.
        </p>

        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Halal Dividend Calendar helps you track dividend dates and plan your income in a faith-aligned way. We are
              committed to protecting your privacy and being transparent about the data we collect and how we use it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Data Controller</h2>
            <p className="text-muted-foreground leading-relaxed">
              PieFolio AI is the data controller for the Halal Dividend Calendar app. For any privacy questions, contact us
              at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline break-all">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>
                <strong>Account Information:</strong> Email address and authentication identifiers when you create an
                account.
              </li>
              <li>
                <strong>App Data:</strong> Dividend tickers, watchlists, favorites, and any preferences you set inside the
                app.
              </li>
              <li>
                <strong>Device Information:</strong> Device type, operating system version, app version, and anonymized
                device identifiers for stability and compatibility.
              </li>
              <li>
                <strong>Usage Data:</strong> Feature usage and crash logs to help us improve the app.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Information We Do Not Collect</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>We do not collect brokerage credentials or bank account details.</li>
              <li>We do not access your contacts, camera, microphone, photos, or location.</li>
              <li>We do not track you across other apps or websites for advertising purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>To provide, maintain, and improve the App and its features.</li>
              <li>To send you dividend-related reminders and app notifications (only if you enable them).</li>
              <li>To respond to support requests and troubleshoot issues.</li>
              <li>To protect against fraud, abuse, and security threats.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Data Sharing</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell your personal data. We only share data with trusted service providers who help us operate the
              App (cloud hosting, authentication, analytics, and crash reporting). These providers are contractually
              obligated to protect your data and process it only on our instructions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              7. Data Security
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We use industry-standard security measures, including encryption in transit (TLS) and at rest, to protect your
              data. Access is limited to authorized personnel only.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              8. Data Retention
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>Account and app data are retained while your account is active.</li>
              <li>When you delete your account, your personal data is removed within 30 days.</li>
              <li>Anonymized usage statistics may be kept longer for product improvement.</li>
              <li>Records required by law (tax, fraud prevention) are retained only as long as legally necessary.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-primary" />
              9. Account Deletion
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You can request deletion of your account and data at any time. We offer two ways:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>
                <strong>In the app:</strong> Open the App, go to <strong>Settings → Account → Delete account</strong>, and
                confirm.
              </li>
              <li>
                <strong>By email:</strong> Send a request from the email address associated with your account to{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20account`}
                  className="text-primary hover:underline break-all"
                >
                  {SUPPORT_EMAIL}
                </a>{" "}
                with the subject <strong>"Delete my account"</strong>.
              </li>
            </ol>
            <p className="text-muted-foreground leading-relaxed mt-4">
              For full details on what is deleted and how long it takes, see our{" "}
              <Link to="/halal-dividend-calendar/delete-account" className="text-primary hover:underline">
                Account Deletion page
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Depending on your location, you may have the right to access, correct, export, or delete your personal data.
              To exercise these rights, contact us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline break-all">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              The App is not directed to children under 13. We do not knowingly collect personal data from children under
              13. If you believe we have collected such data, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes through the App
              or by email. The "Last updated" date at the top of this page reflects the most recent revision.
            </p>
          </section>

          <section className="p-6 rounded-lg bg-muted/50 border border-border">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline break-all">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </section>
        </div>
        <ExploreMore />
      </main>

    </div>
  );
};

export default HalalDividendCalendarPrivacy;
